import { Link } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { Image } from "@/components/image";
import { ACTIVITIES__NEW__ } from "@/lib/constants";

export function Hero() {
  const videoContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const videoContainer = videoContainerRef.current;
    if (!videoContainer) return;

    const mediaQuery = window.matchMedia("(min-width: 64rem)");

    const handleVideo = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        const existingVideo = videoContainer.querySelector("video");
        if (!existingVideo) {
          const video = document.createElement("video");
          video.src = "/videos/minigolf.mp4";
          video.autoplay = true;
          video.muted = true;
          video.loop = true;
          video.playsInline = true;
          video.className =
            "absolute inset-y-0 right-0 z-0 object-cover h-full w-2/3";
          videoContainer.appendChild(video);
        }
      } else {
        const video = videoContainer.querySelector("video");
        if (video) {
          video.remove();
        }
      }
    };

    // Initial check
    handleVideo(mediaQuery);

    // Listen for changes
    mediaQuery.addEventListener("change", handleVideo);

    return () => {
      mediaQuery.removeEventListener("change", handleVideo);
    };
  }, []);

  return (
    <div className="relative grid grid-cols-1 lg:h-svh lg:items-center lg:bg-accent-foreground">
      <div ref={videoContainerRef} />
      <div className="relative grid grid-cols-1 md:grid-cols-4 md:gap-4 md:px-4 md:py-24 lg:mx-auto lg:h-fit lg:w-full lg:max-w-7xl">
        <div className="relative z-10 overflow-hidden bg-background py-8 md:hidden">
          <Image
            alt="Brick texture"
            className="-z-10 absolute top-0 left-0 w-1/4"
            height={220}
            loading="eager"
            src="/images/brick_texture.png"
            width={130}
          />
          <Link to="/">
            <Image
              alt="Logo"
              className="h-8 w-full object-contain"
              height={40}
              loading="eager"
              src="/images/logo.png"
              width={210}
            />
          </Link>
        </div>
        <div className="absolute inset-x-0 bottom-0 h-20 bg-accent md:hidden" />
        {Object.values(ACTIVITIES__NEW__).map((activity, i) => (
          <Link
            className="-mt-10 relative grid h-40 grid-cols-[6rem_auto] items-center gap-6 overflow-hidden rounded-br-[3rem] px-4 pt-10 md:mt-0 md:h-auto md:w-full md:grid-cols-1 md:grid-rows-[6rem_auto] md:justify-items-center md:gap-6 md:rounded-2xl md:pb-10 md:text-center lg:h-fit lg:grid-rows-[8rem_auto] lg:gap-48"
            key={activity.key}
            style={{
              zIndex: Object.values(ACTIVITIES__NEW__).length + 1 - i,
            }}
            {...activity.link}
          >
            <Image
              alt={activity.name}
              className="absolute inset-0 z-0 size-full h-full object-cover object-center"
              fetchPriority="high"
              height={520}
              loading="eager"
              src={activity.brand.backgroundImage}
              width={520}
            />
            <div className="absolute inset-0 z-0 bg-black opacity-40 md:bg-[unset] md:bg-gradient-to-b md:from-transparent md:to-60% md:to-black/90 md:opacity-100 lg:to-90% lg:to-black/80" />
            <Image
              alt={activity.name}
              className="z-10 mx-auto size-full w-14 object-contain object-center md:h-auto md:max-h-full md:w-2/3 lg:w-1/2"
              fetchPriority="high"
              height={160}
              loading="eager"
              src={activity.brand.logo}
              width={160}
            />
            <div className="z-10">
              <h2
                className="font-bold text-2xl md:text-xl lg:text-4xl"
                style={{
                  color: activity.brand.primaryColor,
                }}
              >
                {activity.name}
              </h2>
              <p className="text-base">{activity.shortDescription}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
